/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */
import {
  Avatar,
  ContextualMenu,
  ContextualMenuContent,
  ContextualMenuItem,
  Header,
  Logo,
  SignOutIcon,
  Divider,
  Label,
  SegmentedControl,
  SegmentedControlButton,
} from "@forge/common";
import "./HeaderComponent.scss";
import { useOidc, useOidcIdToken } from "@axa-fr/react-oidc";
import { ConfigurationVariables } from "../../../config";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useEffect, useRef, useState } from "react";
import { toggleTheme } from "../../../store/theme/themeSlice";
import { useFlags } from "launchdarkly-react-client-sdk";
import useLocalStorageChange from "../../../hooks/useLocalStorageChange";
import useUserProfileImage from "../../../hooks/user-profile-image/useUserProfileImage";

interface IProps {
  getShowNav: (showNav: boolean) => void;
  verticalName: string;
  superScript: string;
  suiteName: string;
  suiteNameToggle: boolean;
  onSidebarToggle: () => void;
}

const HeaderComponent = (props: IProps) => {
  const flags = useFlags();
  const themeEnabled = flags?.adminPortalUiToggleTheme;
  const [theme, setTheme] = useState(
    localStorage.getItem("currentTheme") || "light"
  );
  const { logout } = useOidc();
  const userPermissions = useSelector(
    (state: RootState) => state.user.userPermissions
  );
  const { idTokenPayload } = useOidcIdToken();
  const element = document.getElementsByClassName("_MBwSuw");
  if (element?.length > 0) {
    element[0]?.classList?.add("superscript");
  }
  const anchorRef = useRef<any>(null);
  const { userProfileImg } = useUserProfileImage();

  useLocalStorageChange("currentTheme", (updatedTheme) => {
    if (updatedTheme) {
      setTheme(updatedTheme);
    }
  });

  /**Sets selected theme in Redux store */
  const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTheme(e.target.value);
    localStorage.setItem("currentTheme", e.target.value);

    // Dispatch custom event for localStorage changes
    const event = new Event("localStorageChange");
    window.dispatchEvent(event);
  };

  const handleUserSignout = () => {
    logout(ConfigurationVariables.KONG_URL + "logout");
  };
  const userInitials = `${idTokenPayload?.given_name?.charAt(
    0
  )}${idTokenPayload?.family_name?.charAt(0)}`.toUpperCase();

  /*
   * This code snippet creates a new Logo component instance with specific props:
   * - `suite`: Sets the suite name using `props.suiteName`.
   * - `suiteSuperScript`: Adds a superscript next to the suite name using `props.superScript`.
   * - `domain`: Specifies the domain or vertical name with `props.verticalName`.
   * This setup allows for a dynamic rendering of the Logo component based on the passed props.
   */
  const logoElementNew = (
    <Logo
      suite={props?.suiteName}
      suiteSuperScript={props?.superScript}
      domain={props?.verticalName}
    />
  );

  return (
    <div className="custom_header">
      <Header
        showMenuToggle={true}
        logo={logoElementNew}
        rightSlot={
          <>
            <Avatar
              ref={anchorRef}
              aria-label="User Profile"
              data-testid="user-profile"
              interactive={true}
              text={userInitials}
              image={
                userProfileImg ? `data:image/jpeg;base64,${userProfileImg}` : ""
              }
              role="button"
            />
            <ContextualMenu
              anchor={anchorRef}
              position="bottom-right"
              fixed={false}
            >
              <ContextualMenuContent>
                <h3 className="text-base font-bold">
                  {idTokenPayload?.given_name} {idTokenPayload?.family_name}
                </h3>
                <p className="truncate text-color-tertiary dark:text-color-tertiary-dark">
                  {idTokenPayload?.preferred_username}
                </p>
                <p className="truncate text-color-tertiary dark:text-color-tertiary-dark">
                  {userPermissions.user_persona}
                </p>
              </ContextualMenuContent>
              <Divider />

              {/* Theme switch button ------------------------------------- */}
              {themeEnabled && (
                <ContextualMenuContent>
                  <div className="grid grid-cols-[minmax(0,_1fr),_auto] items-center gap-4">
                    <Label htmlFor="theme-control">Theme</Label>
                    <SegmentedControl
                      id="theme-control"
                      onChange={handleThemeChange}
                    >
                      <SegmentedControlButton
                        id="light-theme"
                        value="light"
                        defaultChecked={theme === "light"}
                      >
                        Light
                      </SegmentedControlButton>
                      <SegmentedControlButton
                        id="dark-theme"
                        value="dark"
                        defaultChecked={theme === "dark"}
                      >
                        Dark
                      </SegmentedControlButton>
                    </SegmentedControl>
                  </div>
                </ContextualMenuContent>
              )}

              <Divider />
              {/* Logout button ------------------------------------------- */}
              <ContextualMenuItem
                as="button"
                id="logout"
                label="Logout"
                prefix={<SignOutIcon strokeWidth={2.5} />}
                onClick={handleUserSignout}
              />
            </ContextualMenu>
          </>
        }
        className="main-header color-bg-main-header"
        onMenuClick={() => props.onSidebarToggle()}
      />
    </div>
  );
};

export default HeaderComponent;
